import React, {useCallback, useContext, useEffect, useState} from 'react';
import PropTypes from 'prop-types';
import { useSelector } from 'react-redux';
import { useNavigate } from 'react-router-dom';

// COMPONENTS
import CartEmpty from '../CartEmpty/CartEmpty';
import CartItemActionButtons from '../CartContent/CartItemActionButtons/CartItemActionButtons';
import CartItemCounter from '../CartContent/CartItemCounter/CartItemCounter';
import CartItemDetails from '../CartContent/CartItemDetails/CartItemDetails';
import CartItemValue from '../CartContent/CartItemValue/CartItemValue';
import CartSummary from '../CartSummary/CartSummary';

// DS
import {
  Col,
  VertContainer,
  VertDrawer,
  VertSkeleton,
  VertTypography,
} from '@vertem-core/vertice-ds';

// SERVICES
import PipeProvider from 'Services/Providers/PipeProvider';
import usePipeContext from 'Services/Providers/PipeProvider/usePipeContext';
import { WishListContext } from 'Context/Wishlist';

// STYLES
import 'Assets/styles/default/components/Cart/CartDrawerStyles.less';

// UTILS
import generateUUID from 'Utils/generateUUID';
import ynToBool from 'Utils/ynToBool';
import useGA from "Utils/Ga";
import CartItemProvider from "components/Cart/CartContent/CartItemList/CartItemProvider";
import yn from 'yn';

// CART DRAWER COMPONENT
const CartDrawer = ({ isOpen, openCloseHandler }) => {
  /* Hooks */
  const { eventGaUnlimited } = useGA()
  const { simpleWishList, updateSimpleWishList } = useContext(WishListContext);
  const { callApi } = usePipeContext();

  const navigate = useNavigate();

  const itemsCartReducer = useSelector(({ Cart: data }) => data);
  const ShippingCart = useSelector(({ Shipping: data }) => data);
  const Catalog = useSelector(({ Catalog: data }) => data);
  const Stores = useSelector(({ Stores: data }) => data);

  /* States */
  const [aliasProvider, setAliasProvider] = useState('');

  /* Vars */
  const { cart } = itemsCartReducer;
  const hasProduct = cart && cart?.items && cart?.items?.length || 0 > 0;
  const cartItems = cart?.items || [];

  const isLoading =
    ynToBool(itemsCartReducer?.isLoading) || ynToBool(ShippingCart?.isLoading);

  /* Handlers */
  const goToCartPageHandler = () => {
    navigate('/cart');
    openCloseHandler();
  };

  /* Utils */
  const groupedData = cartItems.reduce((result, item) => {
    const vendorId = item.vendor.id;
    if (!result[vendorId]) {
      result[vendorId] = [];
    }
    result[vendorId].push(item);

    return result;
  }, {});

  const CountItemEvemGa4 = useCallback((item, qt, event) => {
    eventGaUnlimited({
      event,
      currency: Catalog?.currencyConfiguration?.pointsName?.toUpperCase(),
      value: parseFloat(item.price),
    }, {
      items: [
        {
          item_id: item.sku,
          item_name: item.name,
          currency: Catalog?.currencyConfiguration?.pointsName?.toUpperCase(),
          item_brand: item.vendor.name,
          price: parseFloat(item.price),
          quantity: qt
        }
      ]
    })
  }, [ Catalog ])

  const renderCartItems = Object.keys(groupedData || {}).map((vendorId) => {
    const vendorItems = groupedData[vendorId];

    const dataStore = [...Stores.ecommerce, ...Stores.vouchers];

    const vendorEcommerce = dataStore.find(
      (store) => store.supplierId === Number(vendorId)
    );

    const vendorVoucher = dataStore.find(
      (store) => store.supplierId === Number(vendorId)
    );

    const hasVoucher = cartItems.some(
      (item) =>
        item.vendor.type == 'PHYSICALVOUCHER' ||
        item.vendor.type == 'EXTERNALVOUCHERVIRTUAL'
    );

    const nameVendor = vendorEcommerce && vendorEcommerce?.name;
    const nameVendorVoucher = vendorVoucher && vendorVoucher?.name;

    return (
      <Col className="cart-drawer-item">
        <Col
          key={vendorId}
          className="col-12 cart-item-shipping margin-bottom-small vert-bg-light-lighter"
          shape="round"
          verticalSpacing="extra-small"
          spacing="small"
        >
          <VertTypography.Paragraph className="cart-drawer-vendor-name">
            Fornecido e entregue por{' '}
            <span>{hasVoucher ? nameVendorVoucher : nameVendor} </span>
          </VertTypography.Paragraph>
        </Col>

        {vendorItems.map((item) => {
          const vendorId = item?.vendor?.id;
          const totalValue = parseFloat(item.price) * parseFloat(item.quantity);

          return (
            <CartItemProvider item={{
                ...item,
                vendor: {
                  name: hasVoucher ? nameVendorVoucher : nameVendor
                }
            }}
            >
              <div key={item.sku}>
                <CartItemDetails
                  imageAlt={item.name}
                  imageUrl={item.imageUrl}
                  itemName={item.name}
                  itemSeller={item.seller}
                  shippingValue={null}
                  hideActionsButtons
                />
                <Col className="cart-drawer-actions">
                  <Col className="cart-drawer-buttons">
                    <CartItemCounter
                      isLoading={isLoading}
                      quantity={item.quantity}
                      sku={item.sku}
                      vendorId={vendorId}
                      gaCallback={(qt, nqt) => {
                        const quantityDiference = Math.max(qt, nqt) - Math.min(qt, nqt)
                        const event = qt < nqt ? 'add_to_cart': qt > nqt ? 'remove_from_cart': ''
                        if (event) CountItemEvemGa4({...item,
                          vendor: {
                            name: hasVoucher ? nameVendorVoucher : nameVendor
                          }
                        }, quantityDiference, event)
                      }}
                    />
                    <CartItemActionButtons
                      isFavorited={simpleWishList.get(item.sku)}
                      onlyIcons
                      sku={item.sku}
                    />
                  </Col>
                  <CartItemValue value={totalValue} />
                </Col>
                <Col className="divider" />
              </div>
            </CartItemProvider>
          );
        })}
      </Col>
    );
  });
  /* Lifecycles */
  useEffect(() => {
    const aliasProviderUUID = generateUUID();
    setAliasProvider(aliasProviderUUID);
  }, []);

  useEffect(() => {
    if (itemsCartReducer?.finishRequests && !itemsCartReducer?.hasError) {
      const aliasProviderUUID = generateUUID();
      callApi({
        symbol: 'FETCH_CART',
        alias: aliasProviderUUID,
      });
    }
  }, [itemsCartReducer?.finishRequests]);

  useEffect(() => {
    updateSimpleWishList();
  }, []);

  /* Renders */
  return (
    <VertDrawer
      className="cart-drawer-content"
      onClose={openCloseHandler}
      open={isOpen}
      placement="right"
      rootClassName="cart-drawer-wrapper"
    >
      <PipeProvider
        apiSymbols={[{ symbol: 'FETCH_CART', alias: aliasProvider }]}
        loadingComponent={
          <VertContainer spacingX="small">
            <VertSkeleton paragraph={{ rows: 4 }} active />
          </VertContainer>
        }
      >
        <VertTypography.Paragraph className="cart-drawer-title">
          Meu carrinho
        </VertTypography.Paragraph>

        {!hasProduct && (
          <Col className="cart-drawer-empty">
            <CartEmpty openCloseHandler={openCloseHandler} />
          </Col>
        )}

        {hasProduct && (
          <>
            <Col className="cart-drawer-items-wrapper">{((renderCartItems && renderCartItems.length) && (renderCartItems))}</Col>
            <Col className="cart-drawer-summary">
              <CartSummary
                hideShipping={true}
                onClickButton={goToCartPageHandler}
                hasError={false}
                isLoading={isLoading}
                hasItemsVirtualVoucher={
                  itemsCartReducer?.hasItemsVirtualVoucher
                }
                buttonText="Ir para o carrinho"
              />
            </Col>
          </>
        )}
      </PipeProvider>
    </VertDrawer>
  );
};

CartDrawer.propTypes = {
  isOpen: PropTypes.bool.isRequired,
  openCloseHandler: PropTypes.func.isRequired,
};

export default CartDrawer;
